import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Hook to manage a single URL search parameter.
 * Standardized to return null instead of undefined for "no value".
 *
 * @param paramName - The URL parameter name
 * @returns [value, setValue] tuple where value is the current param value (or null)
 */
export const useURLParamState = (
  paramName: string
): readonly [string | null, (newValue: string | null) => void] => {
  const [search, setSearch] = useSearchParams();

  const value = useMemo(() => search.get(paramName), [paramName, search]);

  const setValue = useCallback(
    (newValue: string | null) => {
      const next = new URLSearchParams(search);
      if (newValue === null || newValue === "") {
        next.delete(paramName);
      } else {
        next.set(paramName, newValue);
      }
      setSearch(next);
    },
    [paramName, search, setSearch]
  );

  return [value, setValue] as const;
};
